<template>
  <b-container>
    <b-row class="headline" align-v="center">
      <b-col class="header" cols="*" v-if="album.id">
        Edit Album
      </b-col>
      <b-col class="header" cols="*" v-else>
        Create Album
      </b-col>
      <b-col class="cancel">
        <router-link :to="cancelLink">cancel</router-link>
      </b-col>
    </b-row>
    <b-row class="command-row" align-v="center">
      <b-col>
        <h1 v-if="album.id">
          {{ album.title }}
        </h1>
        <h1 v-else>
          Create New Album
        </h1>
      </b-col>
      <b-col cols="*" class="actions">
        <LinkButton v-b-modal.confirmDelete :u="true" v-if="album.id">Delete album</LinkButton>
        <b-button class="btn-rainbow" @click="saveAlbum">Save</b-button>
      </b-col>
    </b-row>

    <b-row class="album-details first">
      <b-col class="dropzone" v-if="!hasCover">
        <div class="big-info">
          Select album cover
        </div>
        <b-button class="btn-rainbow" @click="selectCoverFile">click here to browse</b-button>
        <input type="file" id="coverfile" ref="fileInput" @change="onCoverFileSelected">
        <div class="info">
          Supported formats: .png, .jpg<br>
          370 x 370 px minimum
        </div>
      </b-col>

      <b-col v-if="hasCover" class="coverimage">
        <img :src="coverFileUrl">
        <b-button variant="danger" size="xs" class="remove" @click="removeCover">
          <font-awesome-icon icon="times" />
        </b-button>
      </b-col>
      <b-col>
        <b-form-group label="Album Title"
                      :invalidFeedback="errorTitle"
                      :state="stateTitle">
          <b-form-input type="text" required placeholder="Title of the album" v-model="album.title" :state="stateTitle" />
        </b-form-group>

        <b-form-group id="yearGroup"
                      label="Release Date"
                      :invalidFeedback="errorReleaseDate"
                      :state="stateReleaseDate"
                      label-for="year" class="short">
          <datepicker id="startDate"
                      calendar-class="release_date"
                      format="MM/dd/yyyy"
                      v-model="releaseDate"></datepicker>
        </b-form-group>

        <b-form-group id="visibilityGroup"
                      label="Visibility"
                      label-for="visibility"
                      :invalidFeedback="errorVisibility"
                      :state="stateVisibility">
          <b-form-select v-model="album.visibility" id="visibility" :options="albumVisibilities" :state="stateVisibility" />
        </b-form-group>

        <b-form-group id="priceGroup"
                      label="Sale Price (USD)"
                      description="Free, or minimum $0.50"
                      :invalidFeedback="errorPrice"
                      :state="statePrice"
                      label-for="price" class="short">
          <b-form-input type="text" required placeholder="Amount" v-model="album.price" :state="statePrice" />
        </b-form-group>
        <b-form-checkbox v-model="album.can_pay_more" size="sm">
            Let fans back you more, and they might pay a little extra
        </b-form-checkbox>
      </b-col>
    </b-row>

    <b-row class="add-songs-headline">
      <b-col>
        <h5>Add songs to your album (audio files required for each video)</h5>
      </b-col>
    </b-row>

    <SongListEditor v-model="songs"
                    :canAdd="true"
                    :canRemove="true"
                    :canSort="true"
                    :availableSongs="availableSongs">
    </SongListEditor>

    <b-row class="command-row bottom" align-v="center">
      <b-col>
        <LinkButton v-b-modal.confirmDelete :u="true" v-if="album.id">Delete album</LinkButton>
        <b-button class="btn-rainbow" @click="saveAlbum">Save</b-button>
      </b-col>
    </b-row>

    <b-modal id="confirmDelete"
             ref="confirmDelete"
             title="Delete album"
             @ok="deleteAlbum"
             centered>
      <div class="content">
        <p>
          Are you sure you want to delete this album?
        </p>
      </div>
    </b-modal>
    <b-modal id="songNotPublishedModal"
             ref="songNotPublishedModal"
             title="Error"
             ok-only="true"
             centered>
      <div class="content">
        <p>
          Unable to publish album. Each song must be made "public" individually before publishing.
        </p>
      </div>
    </b-modal>
  </b-container>
</template>

<style scoped lang="scss">
  @import '@/assets/custom.scss';
  @import '~bootstrap/scss/mixins/_breakpoints.scss';

  .headline {
    margin-top: 1.5em;
    margin-bottom: 1em;

    box-shadow: 0 4px 2px -2px rgba(0, 0, 0, 0.5);
    margin-bottom: 2em;

    .header {
      font-weight: bold;
      padding: 1em;
      border-bottom: 2px solid white;
    }

    .cancel {
      text-align: right;
    }

    a {
      color: $white;
    }
  }
  .actions {
    @include media-breakpoint-down(xs) {
      flex: 0 0 100%;
      max-width: 100%;
      padding: 5px 15px 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }

  .command-row {
    margin-bottom: 1em;

    a {
      color: $white;
      text-decoration: underline;
    }

    .btn-rainbow {
      margin-left: 2em;
      padding-left: 2em;
      padding-right: 2em;
    }

    h1 {
      font-size: 1.5em;
    }

    &.bottom {
      margin-top: 1em;
      text-align: right;
    }
  }

  .album-details {
    margin: 0 -15px;
    padding: 1em 0 1em 1em;

    &.first {
      background-image: linear-gradient(to bottom, #1e1f36, rgba(0, 0, 0, 0));
    }

    &.last {
      box-shadow: 0 4px 2px -2px rgba(0, 0, 0, 0.5);
    }

    .dropzone {
      // extra width due to padding
      width: 400px;
      height: 370px;
      flex-basis: 400px;
      flex-grow: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;

      border-image: linear-gradient(107deg, $darkish-pink, $blue-purple) 1;
      border-width: 1px;
      border-style: solid;
      box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.64);

      align-items: center;
      @include media-breakpoint-down(xs) {
        width: 100%;
        /* height: 270px; */
        flex-basis: calc(100% - 15px);
        margin-bottom: 1em;
      }

      .big-info {
        margin-bottom: 1em;
      }

      .info {
        text-align: center;
        margin-top: 1em;
        font-size: 0.8em;
        color: $steel;
      }
    }

    .coverimage {
      position: relative;
      width: 400px;
      flex-grow: 0;
      flex-basis: 400px;
      margin: 0 auto;
      @include media-breakpoint-down(xs) {
        width: 320px;
        flex-basis: 320px;
        margin-bottom: 1em;
      }

      img {
        width: 370px;
        height: 370px;
        object-fit: cover;
        @include media-breakpoint-down(xs) {
          width: 280px;
          height: 280px;
        }
      }

      .remove {
        position: absolute;
        top: 1em;
        right: 2em;
      }
    }
  }

  #coverfile {
    display: none;
  }

  .short input.form-control {
    width: 8em;
  }

  .add-songs-headline {
    .col {
      margin-top: 1em;
    }

    border-bottom: 1px solid $slate-blue;
    margin-bottom: 10px;
  }
</style>

<script>
import moment from '@/utils/moment'
import { mapActions } from 'vuex'
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'
import Datepicker from 'vuejs-datepicker'

import LinkButton from '@/components/widgets/LinkButton'
import SongListEditor from '@/components/widgets/SongListEditor'

import { albumVisibilities } from '@/store/modules/song'
import { parseErrors, mapErrorStates } from '@/store/helpers'

export default {
  name: 'EditAlbum',
  components: {
    FontAwesomeIcon,
    Datepicker,
    SongListEditor,
    LinkButton
  },
  data () {
    return {
      errors: {},
      coverFile: null,
      removedPublishedCoverFile: false,
      uploadProgress: null,
      addingSong: false,
      albumVisibilities: albumVisibilities,
      releaseDate: new Date(),
      album: {},
      songs: [],
      allSongs: []
    }
  },
  computed: {
    hasCover () {
      return this.coverFile || this.album.cover
    },
    coverFileUrl () {
      if (this.coverFile) return URL.createObjectURL(this.coverFile)
      if (this.album.cover) return this.album.cover
      return null
    },
    availableSongs () {
      let songIds = []
      for (let song of this.songs) {
        songIds.push(song.id)
      }

      let songs = []
      for (let song of this.allSongs) {
        if (!song.has_digital_download) continue
        if (songIds.indexOf(song.id) !== -1) continue
        songs.push(song)
      }
      return songs
    },
    cancelLink () {
      if (this.album && this.album.id) {
        return {name: 'artist-album-list', params: {visibility: this.album.visibility}}
      } else {
        return {name: 'artist-album-list', params: {visibility: 'draft'}}
      }
    },
    ...mapErrorStates(['title', 'release_date', 'price', 'visibility'])
  },
  created () {
    this.fetchData()
    this.fetchAllSongs()
  },
  watch: {
    '$route': 'fetchData'
  },
  methods: {
    async fetchData () {
      if (!this.$route.params.id) {
        this.album = {}
        return
      }

      let artist = this.$store.getters['artist/artist']
      let album = await this.$store.dispatch('song/getAlbum', {id: this.$route.params.id})
      if (album.artist.id !== artist.id) {
        this.$router.push({name: 'artist-dashboard'})
        return
      }

      this.parseAlbum(album)
    },
    async fetchAllSongs () {
      let response = await this.$store.dispatch('song/listOwnSongs')
      let allSongs = []

      while (response && response.count > 0) {
        for (let item of response.results) {
          allSongs.push(item)
        }
        response = await response.next()
      }

      this.allSongs = allSongs
    },
    parseAlbum (album) {
      this.album = album
      this.songs = album.songs
      this.releaseDate = moment(album.release_date).toDate()
    },
    selectCoverFile () {
      this.$refs.fileInput.click()
    },
    onCoverFileSelected () {
      if (this.$refs.fileInput.files.length !== 1) return
      this.coverFile = this.$refs.fileInput.files[0]
      this.$refs.fileInput.value = ''
    },
    removeCover () {
      this.coverFile = null

      if (this.album.cover) {
        this.album.cover = null
        this.removedPublishedCoverFile = true
      }
    },
    hasNotPublishedSong () {
      return this.songs.find(song => song.video_visibility !== 'public')
    },
    async saveAlbum () {
      this.album.song_ids = []
      for (let song of this.songs) {
        this.album.song_ids.push(song.id)
      }
      if ((this.album.visibility !== 'draft') && this.hasNotPublishedSong()) {
        this.$refs.songNotPublishedModal.show()
        return
      }

      this.album.release_date = moment(this.releaseDate).format('YYYY-MM-DD')

      this.showModal('Publishing album ...')
      let isCreate = !this.album.id
      let resp
      try {
        resp = await this.$store.dispatch(isCreate ? 'song/createAlbum' : 'song/updateAlbum', this.album)
      } catch (e) {
        this.$store.dispatch('flash/showMessage', {message: 'Error while saving album', type: 'error'})
        this.hideModal()
        if (e.response && e.response.data) {
          this.errors = parseErrors(e.response.data)
        }
        return
      }

      this.parseAlbum(resp)

      if (this.removedPublishedCoverFile) {
        this.showModal('Removing album cover ...')
        await this.$store.dispatch('song/deleteAlbumCover', { id: this.album.id })
        this.removedPublishedCoverFile = false
      }

      if (this.coverFile) {
        this.showModal('Uploading album cover ...')
        try {
          await this.$store.dispatch('song/setAlbumCover', { id: this.album.id, file: this.coverFile })
        } catch (e) {
          this.$store.dispatch('flash/showMessage', {message: 'Unsupported image type', type: 'error'})
          this.hideModal()
          return
        }
      }

      if (isCreate) {
        this.$store.dispatch('flash/showMessage', {message: 'Album created'})
      } else {
        this.$store.dispatch('flash/showMessage', {message: 'Album updated'})
      }

      this.hideModal()
      this.$router.push({name: 'artist-album-list', params: {visibility: this.album.visibility}})
    },
    async deleteAlbum () {
      if (!this.album.id) return

      try {
        await this.$store.dispatch('song/deleteAlbum', { id: this.album.id })
      } catch (e) {
        let msg = 'Can\'t delete album'
        if (e.response && e.response.data && e.response.data.detail) {
          msg = 'Can\'t delete album: ' + e.response.data.detail
        }
        this.$store.dispatch('flash/showMessage', {message: msg, type: 'error'})
        return
      }

      this.$store.dispatch('flash/showMessage', {message: 'Deleted album'})
      this.$router.push({name: 'artist-album-list', params: {visibility: 'public'}})
    },
    ...mapActions('flash', ['showModal', 'hideModal'])
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/artist/EditAlbum.vue